import React, { useState, useEffect } from "react";
import axios from "axios";
import "./RulesEditor.css";

interface RulesEditorProps {
  customRules: string;
  onRulesChange: (rules: string) => void;
  useCustomRules: boolean;
  onToggleCustomRules: (enabled: boolean) => void;
}

interface DefaultRulesResponse {
  rules: string;
  version: string;
  effective_date: string;
}

const RulesEditor: React.FC<RulesEditorProps> = ({
  customRules,
  onRulesChange,
  useCustomRules,
  onToggleCustomRules,
}) => {
  const [defaultRules, setDefaultRules] = useState<string>("");
  const [editableRules, setEditableRules] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [rulesInfo, setRulesInfo] = useState<{
    version: string;
    effective_date: string;
  } | null>(null);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  // Load default rules on component mount
  useEffect(() => {
    loadDefaultRules();
  }, []);

  // Update editable rules when custom rules or toggle changes
  useEffect(() => {
    if (useCustomRules) {
      setEditableRules(customRules || defaultRules);
    } else {
      setEditableRules(defaultRules);
    }
  }, [useCustomRules, customRules, defaultRules]);

  const loadDefaultRules = async () => {
    setLoading(true);
    setError(null);

    try {
      console.log("Loading default rules from API...");
      const response = await axios.get<DefaultRulesResponse>(
        `${process.env.REACT_APP_API_URL}/api/rules/default`
      );

      const { rules, version, effective_date } = response.data;
      console.log("Default rules loaded successfully:", {
        length: rules.length,
        version,
        effective_date,
      });

      setDefaultRules(rules);
      setEditableRules(rules);
      setRulesInfo({ version, effective_date });
    } catch (err: any) {
      console.error("Error loading default rules:", err);
      const errorMessage =
        err.response?.data?.details ||
        err.message ||
        "Failed to load default rules";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const validateRules = (rules: string): string[] => {
    const errors: string[] = [];

    // Check for required sections
    const requiredSections = [
      "AI JUDGE™ RULES OF PROCEDURE",
      "Article 1 — Definitions",
      "Article 2 — Scope and Hierarchy of Norms",
      "Article 8 — Catalogue of Remedies",
    ];

    requiredSections.forEach((section) => {
      if (!rules.includes(section)) {
        errors.push(`Missing required section: ${section}`);
      }
    });

    // Check minimum length
    if (rules.length < 1000) {
      errors.push("Rules must be at least 1000 characters long");
    }

    // Check maximum length (reasonable limit for AI processing)
    if (rules.length > 100000) {
      errors.push("Rules must be less than 100,000 characters");
    }

    return errors;
  };

  const handleRulesChange = (value: string) => {
    setEditableRules(value);
    const errors = validateRules(value);
    setValidationErrors(errors);

    // Always propagate rules, but frontend will show validation errors
    // Backend will do final validation before processing
    onRulesChange(value);
  };

  const handleToggleCustomRules = (enabled: boolean) => {
    onToggleCustomRules(enabled);

    if (!enabled) {
      // Reset to default rules
      setEditableRules(defaultRules);
      setValidationErrors([]);
      onRulesChange("");
    } else {
      // Start with current custom rules or default rules
      const startingRules = customRules || defaultRules;
      setEditableRules(startingRules);
      handleRulesChange(startingRules);
    }
  };

  const handleResetToDefault = () => {
    setEditableRules(defaultRules);
    setValidationErrors([]);
    onRulesChange(defaultRules);
  };

  const getRulesPreview = (rules: string): string => {
    const lines = rules.split("\n");
    const previewLines = lines.slice(0, 5);
    return previewLines.join("\n") + (lines.length > 5 ? "\n..." : "");
  };

  const getWordCount = (text: string): number => {
    return text
      .trim()
      .split(/\s+/)
      .filter((word) => word.length > 0).length;
  };

  if (loading) {
    return (
      <div className="rules-editor">
        <div className="rules-loading">
          <div className="loading-spinner"></div>
          <p>Loading default rules...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rules-editor">
        <div className="rules-error">
          <p>Error: {error}</p>
          <button onClick={loadDefaultRules} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="rules-editor">
      <div className="rules-header">
        <h3>🏛️ Judge Rules Configuration</h3>
        {rulesInfo && (
          <div className="rules-info">
            <span>
              Version {rulesInfo.version} • {rulesInfo.effective_date}
            </span>
          </div>
        )}
      </div>

      <div className="rules-toggle-section">
        <label className="rules-toggle">
          <input
            type="checkbox"
            checked={useCustomRules}
            onChange={(e) => handleToggleCustomRules(e.target.checked)}
          />
          <span className="toggle-slider"></span>
          <span className="toggle-label">
            {useCustomRules ? "Custom Rules" : "Default Rules"}
          </span>
        </label>

        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className="expand-toggle"
        >
          {isExpanded ? "🔼 Collapse" : "🔽 Expand Rules"}
        </button>
      </div>

      {/* Rules Preview (when collapsed) */}
      {!isExpanded && (
        <div className="rules-preview">
          <div className="preview-header">
            <span className="preview-label">
              {useCustomRules
                ? "Custom Rules Preview:"
                : "Default Rules Preview:"}
            </span>
            <div className="preview-stats">
              {getWordCount(editableRules)} words • {editableRules.length}{" "}
              characters
            </div>
          </div>
          <pre className="preview-text">{getRulesPreview(editableRules)}</pre>
        </div>
      )}

      {/* Full Rules Editor (when expanded) */}
      {isExpanded && (
        <div className="rules-content">
          <div className="rules-editor-header">
            <div className="editor-stats">
              <span>{getWordCount(editableRules)} words</span>
              <span>{editableRules.length} characters</span>
              {validationErrors.length > 0 && (
                <span className="validation-status error">
                  ❌ {validationErrors.length} validation error
                  {validationErrors.length > 1 ? "s" : ""}
                </span>
              )}
              {validationErrors.length === 0 && editableRules.length > 0 && (
                <span className="validation-status success">✅ Valid</span>
              )}
            </div>
          </div>

          {/* Validation Errors */}
          {validationErrors.length > 0 && (
            <div className="validation-errors">
              <h4>⚠️ Validation Errors:</h4>
              <ul>
                {validationErrors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          )}

          <textarea
            className={`rules-textarea ${
              validationErrors.length > 0 ? "error" : ""
            }`}
            value={editableRules}
            onChange={(e) => handleRulesChange(e.target.value)}
            placeholder={
              useCustomRules
                ? "Enter your custom rules here..."
                : "Default rules (read-only)"
            }
            readOnly={!useCustomRules}
            rows={25}
          />

          <div className="rules-actions">
            {useCustomRules && (
              <>
                <button
                  type="button"
                  onClick={handleResetToDefault}
                  className="reset-button"
                >
                  🔄 Reset to Default
                </button>
                <button
                  type="button"
                  onClick={() => navigator.clipboard.writeText(editableRules)}
                  className="copy-button"
                >
                  📋 Copy Rules
                </button>
              </>
            )}

            <div className="action-spacer"></div>

            <button
              type="button"
              onClick={() => setIsExpanded(false)}
              className="collapse-button"
            >
              🔼 Collapse Editor
            </button>
          </div>
        </div>
      )}

      {/* Help Text */}
      <div className="rules-help">
        <p>
          {useCustomRules ? (
            <>
              <strong>Custom Rules Mode:</strong> You can modify the judge's
              rules. Make sure to include all required sections for proper
              dispute resolution.
            </>
          ) : (
            <>
              <strong>Default Rules Mode:</strong> The standard AI Judge™ rules
              will be used. Toggle to "Custom Rules" to create your own version.
            </>
          )}
        </p>
      </div>
    </div>
  );
};

export default RulesEditor;
